// สถิติการละหมาด
//
// หน้านี้ตอบสามคำถามที่ต่างกัน จึงแยกเป็นสามส่วน ไม่ใช่ตารางเดียวยาวๆ:
//   1. ดีขึ้นหรือแย่ลง        -> กราฟแนวโน้มรายวัน
//   2. ใครที่ต้องเข้าไปดูแล   -> ตารางคนที่ขาดบ่อย (ของชิ้นที่เอาไปทำงานต่อได้จริง)
//   3. เมื่อวานเกิดอะไรขึ้น   -> ตารางรายรอบของวันที่เลือก
//
// ตัวกรองอยู่แถวเดียวด้านบนคุมทั้งหน้า ไม่ใช่แยกกรองในแต่ละการ์ด
// ไม่งั้นตัวเลขสามส่วนจะมาจากคนละช่วงเวลาโดยไม่มีอะไรบอก
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import Swal from 'sweetalert2';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import axiosInstance from '../services/axiosInterceptor';
import ThaiDateInput from '../components/ThaiDateInput'

const API = process.env.REACT_APP_API || 'http://localhost:3002/api';

// ทุกคนละหมาดที่มัสยิดหลังเดียวกัน กราฟจึงมีเส้นเดียว ไม่ต้องมีคำอธิบายสี
// (สองเส้นขึ้นไปต้องมีคำอธิบายสีเสมอ ส่วนเส้นเดียวหัวข้อการ์ดบอกได้แล้วว่าคืออะไร)
// สีนี้ผ่านการตรวจ contrast กับพื้นทั้งสองโหมดด้วย validate_palette.js
// เปลี่ยนสีเมื่อไหร่ต้องรันตรวจใหม่ อย่าเลือกด้วยสายตา
const SERIES_COLOR = { light: '#2a78d6', dark: '#3987e5' };

const ymd = (d) => {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};
const today = () => ymd(new Date());
const daysAgo = (n) => ymd(new Date(Date.now() - n * 86400000));

const thaiDay = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('th-TH', { day: 'numeric', month: 'short' });

const pct = (v) => (v == null ? '-' : `${v.toFixed(1)}%`);

export default function PrayerReport() {
  // เก็บค่า skin ไว้ใช้จริง ไม่ใช่ทิ้งเหมือนหน้าอื่น เพราะกราฟต้องสลับชุดสีตามธีม
  // Header เรียก onSkin ทุกครั้งที่ผู้ใช้สลับ ค่านี้จึงอัปเดตเองโดยไม่ต้องฟัง event
  const [skin, setSkin] = useState(localStorage.getItem('skin-mode') ? 'dark' : '');
  const isDark = skin === 'dark';

  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(today());
  const [day, setDay] = useState(today());

  const [range, setRange] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [daily, setDaily] = useState([]);
  const [loading, setLoading] = useState(false);
  const [student, setStudent] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, l, d] = await Promise.all([
        axiosInstance.get(`${API}/prayer/summary/range`, { params: { from, to } }),
        axiosInstance.get(`${API}/prayer/absent-leaders`, { params: { from, to, minAbsent: 3, limit: 50 } }),
        axiosInstance.get(`${API}/prayer/summary/daily`, { params: { date: day } }),
      ]);
      setRange(r.data);
      setLeaders(l.data);
      setDaily(d.data);
    } catch (error) {
      Swal.fire('อ่านสถิติไม่สำเร็จ', error?.response?.data?.message || 'ลองใหม่อีกครั้ง', 'error');
    } finally {
      setLoading(false);
    }
  }, [from, to, day]);

  useEffect(() => { load(); }, [load]);

  // แกน x ใช้วันที่ที่มีข้อมูลจริงเท่านั้น ไม่เติมวันที่ว่างให้ครบช่วง
  // วันหยุดที่ไม่มีการเช็คชื่อไม่ใช่ "อัตรา 0%" มันคือไม่มีข้อมูล
  // ลากเส้นผ่านศูนย์จะทำให้กราฟดูเหมือนมีวันที่ไม่มีใครมาละหมาดเลย
  const dates = useMemo(() => range.map((r) => r.date), [range]);

  // rate เป็น null ได้เมื่อวันนั้นทุกคนได้รับยกเว้น ApexCharts จะเว้นช่องว่างให้เอง
  // ไม่ลากผ่านศูนย์ เพราะ "ไม่มีข้อมูล" ไม่ใช่ "อัตรา 0%"
  const series = useMemo(() => [{
    name: 'อัตราการเข้าละหมาด',
    data: range.map((r) => r.rate),
  }], [range]);

  const colors = useMemo(() => [SERIES_COLOR[isDark ? 'dark' : 'light']], [isDark]);

  // ยอดรวมทั้งช่วง คิดจากผลรวมของตัวตั้งกับตัวหาร ไม่ใช่ค่าเฉลี่ยของอัตรารายวัน
  // เฉลี่ยของอัตราจะให้น้ำหนักวันที่มีคนน้อยเท่ากับวันที่มีคนเยอะ
  const overall = useMemo(() => {
    const expected = range.reduce((sum, r) => sum + r.expected, 0);
    const attended = range.reduce((sum, r) => sum + r.attended, 0);
    const absent = range.reduce((sum, r) => sum + r.absent, 0);
    return {
      days: range.length,
      absent,
      rate: expected > 0 ? (attended / expected) * 100 : null,
    };
  }, [range]);

  const chartOptions = useMemo(() => ({
    chart: {
      type: 'line',
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: 'inherit',
      background: 'transparent',
      animations: { enabled: false },
    },
    theme: { mode: isDark ? 'dark' : 'light' },
    colors,
    stroke: { curve: 'straight', width: 2 },       // เส้นบาง ไม่ใช่แถบหนา
    markers: { size: 4, strokeWidth: 0, hover: { sizeOffset: 2 } },  // 8px เส้นผ่านศูนย์กลาง
    // ไม่ติดตัวเลขบนทุกจุด — ตัวเลขทุกค่าอ่านได้จากตารางด้านล่างและจาก tooltip
    dataLabels: { enabled: false },
    grid: {
      borderColor: isDark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.07)',
      strokeDashArray: 0,                          // เส้นตาราง "ทึบ" เส้นประอ่านเป็นค่าประมาณ
      xaxis: { lines: { show: false } },
      padding: { left: 8, right: 16, top: 0, bottom: 0 },
    },
    xaxis: {
      categories: dates.map(thaiDay),
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
      labels: { style: { fontSize: '11px' }, rotate: -45, rotateAlways: false, hideOverlappingLabels: true },
    },
    yaxis: {
      min: 0,
      max: 100,
      tickAmount: 5,
      labels: { formatter: (v) => `${Math.round(v)}%`, style: { fontSize: '11px' } },
    },
    legend: { show: false },   // เส้นเดียว หัวข้อการ์ดบอกอยู่แล้วว่าคืออะไร
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (v) => (v == null ? 'ไม่มีรอบ' : `${v.toFixed(1)}%`) },
    },
    noData: { text: 'ยังไม่มีรอบที่ปิดแล้วในช่วงนี้' },
  }), [colors, dates, isDark]);

  const openStudent = async (stId, name) => {
    try {
      const { data } = await axiosInstance.get(`${API}/prayer/student/${stId}/summary`, { params: { from, to } });
      setStudent({ ...data, name });
    } catch (error) {
      Swal.fire('อ่านสถิติรายคนไม่สำเร็จ', error?.response?.data?.message || 'ลองใหม่อีกครั้ง', 'error');
    }
  };

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-2 p-lg-4">
        <div className="mb-3">
          <h4 className="main-title mb-0">สถิติการละหมาด</h4>
          <div className="fs-xs text-secondary">นับเฉพาะรอบที่ปิดแล้ว รอบที่ยังเปิดอยู่ยังไม่มีผล</div>
        </div>

        {/* ตัวกรองแถวเดียวคุมทั้งหน้า */}
        <Card className="card-one mb-3">
          <Card.Body className="p-2 p-md-3">
            <Row className="g-2 align-items-end">
              <Col xs={6} md={3}>
                <Form.Label className="fs-xs text-secondary mb-1">ตั้งแต่</Form.Label>
                <ThaiDateInput value={from} max={to} onChange={v => setFrom(v)} />
              </Col>
              <Col xs={6} md={3}>
                <Form.Label className="fs-xs text-secondary mb-1">ถึง</Form.Label>
                <ThaiDateInput value={to} min={from} max={today()} onChange={v => setTo(v)} />
              </Col>
              <Col xs={12} md={6}>
                <Form.Label className="fs-xs text-secondary mb-1">ดูรายรอบของวันที่</Form.Label>
                <ThaiDateInput value={day} max={today()} onChange={v => setDay(v)} />
              </Col>
            </Row>
            <div className="d-flex flex-wrap gap-2 mt-2">
              {[
                { label: '7 วัน', days: 6 },
                { label: '30 วัน', days: 29 },
                { label: '90 วัน', days: 89 },
              ].map((preset) => (
                <Button
                  key={preset.label}
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => { setFrom(daysAgo(preset.days)); setTo(today()); }}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </Card.Body>
        </Card>

        <Row className="g-2 mb-3">
          {[
            { label: 'อัตราการเข้าละหมาด', value: pct(overall.rate), variant: 'primary' },
            { label: 'ขาดรวม (ครั้ง)', value: overall.absent.toLocaleString('th-TH'), variant: 'danger' },
            { label: 'วันที่มีข้อมูล', value: overall.days.toLocaleString('th-TH'), variant: 'secondary' },
          ].map((box) => (
            <Col xs={12} md={4} key={box.label}>
              <Card className="card-one h-100">
                <Card.Body className="p-3">
                  <div className={`fs-1 fw-bold lh-1 text-${box.variant}`}>{box.value}</div>
                  <div className="fs-xs text-secondary mt-1">{box.label}</div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* กราฟแนวโน้ม — ตอบว่าดีขึ้นหรือแย่ลง ตัวเลขทุกค่ามีในตารางถัดไปด้วย */}
        <Card className="card-one mb-3" style={{ opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
          <Card.Header>
            <Card.Title as="h6" className="mb-0">อัตราการเข้าละหมาดรายวัน</Card.Title>
          </Card.Header>
          <Card.Body>
            <div className="fs-xs text-secondary mb-2">
              คิดจากคนที่ต้องมาจริง คือหักคนที่ลา กลับบ้าน หรือป่วยออกจากตัวหารแล้ว
            </div>
            <ReactApexChart options={chartOptions} series={series} type="line" height={300} />
          </Card.Body>
        </Card>

        {/* ตารางคู่ของกราฟ — ทุกค่าที่กราฟแสดงต้องอ่านได้โดยไม่ต้องพึ่งสี */}
        {range.length > 0 && (
          <Card className="card-one mb-3">
            <Card.Header><Card.Title as="h6" className="mb-0">ตัวเลขรายวัน</Card.Title></Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table className="mb-0" size="sm" hover>
                  <thead>
                    <tr>
                      <th>วันที่</th>
                      <th className="text-end">ต้องมา</th>
                      <th className="text-end">มา</th>
                      <th className="text-end">ขาด</th>
                      <th className="text-end">ยกเว้น</th>
                      <th className="text-end">อัตรา</th>
                    </tr>
                  </thead>
                  <tbody style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {range.map((r) => (
                      <tr key={r.date}>
                        <td>{thaiDay(r.date)}</td>
                        <td className="text-end">{r.expected}</td>
                        <td className="text-end">{r.attended}</td>
                        <td className="text-end text-danger">{r.absent}</td>
                        <td className="text-end text-secondary">{r.excused}</td>
                        <td className="text-end fw-semibold">{pct(r.rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* ของชิ้นที่เอาไปทำงานต่อได้จริง — ใครที่ต้องเข้าไปดูแล */}
        <Card className="card-one mb-3">
          <Card.Header>
            <Card.Title as="h6" className="mb-0">
              ขาดละหมาดบ่อย ({leaders.length} คน)
            </Card.Title>
          </Card.Header>
          <Card.Body className="p-0">
            {leaders.length === 0 ? (
              <div className="text-center text-secondary py-4">ไม่มีนักเรียนที่ขาดตั้งแต่ 3 ครั้งขึ้นไปในช่วงนี้</div>
            ) : (
              <div className="table-responsive">
                <Table className="mb-0" size="sm" hover>
                  <thead>
                    <tr>
                      <th>ชื่อ</th>
                      <th>เลขประจำตัว</th>
                      <th>อาคาร</th>
                      <th className="text-end">ขาด (ครั้ง)</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {leaders.map((row) => (
                      <tr key={row.st_id}>
                        <td>{row.st_name}</td>
                        <td className="text-secondary">{row.st_idnumber}</td>
                        <td className="text-secondary">{row.dorm_name || '-'}</td>
                        <td className="text-end">
                          <Badge bg={row.absent_count >= 20 ? 'danger' : row.absent_count >= 10 ? 'warning' : 'secondary'}>
                            {row.absent_count}
                          </Badge>
                        </td>
                        <td className="text-end">
                          <Button size="sm" variant="link" className="p-0" onClick={() => openStudent(row.st_id, row.st_name)}>
                            ดูรายคน
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        <Card className="card-one">
          <Card.Header>
            <Card.Title as="h6" className="mb-0">รายรอบของวันที่ {thaiDay(day)}</Card.Title>
          </Card.Header>
          <Card.Body className="p-0">
            {daily.length === 0 ? (
              <div className="text-center text-secondary py-4">ไม่มีรอบในวันนี้</div>
            ) : (
              <div className="table-responsive">
                <Table className="mb-0" size="sm" hover>
                  <thead>
                    <tr>
                      <th>เวลาละหมาด</th>
                      <th>สถานะ</th>
                      <th className="text-end">เข้าเกณฑ์</th>
                      <th className="text-end">แตะบัตร</th>
                      <th className="text-end">มา</th>
                      <th className="text-end">ขาด</th>
                      <th className="text-end">ยกเว้น</th>
                      <th className="text-end">อัตรา</th>
                    </tr>
                  </thead>
                  <tbody style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {daily.map((r) => (
                      <tr key={r.prayer}>
                        <td>{r.prayer_label}</td>
                        <td>
                          <Badge
                            bg={r.status === 'finalized' ? 'secondary' : r.status === 'open' ? 'success' : 'light'}
                            text={r.status === 'none' ? 'dark' : undefined}
                          >
                            {r.status === 'finalized' ? 'ปิดแล้ว' : r.status === 'open' ? 'กำลังเปิด' : 'ยังไม่ถึงเวลา'}
                          </Badge>
                        </td>
                        <td className="text-end">{r.eligible == null ? '-' : r.eligible}</td>
                        <td className="text-end">{r.scanned == null ? '-' : r.scanned}</td>
                        <td className="text-end text-success">{r.attended}</td>
                        <td className="text-end text-danger">{r.absent}</td>
                        <td className="text-end text-secondary">{r.excused}</td>
                        <td className="text-end fw-semibold">{pct(r.rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        <Modal show={!!student} onHide={() => setStudent(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title as="h6">{student?.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {student && (
              <>
                <div className="fs-xs text-secondary mb-3">
                  {thaiDay(student.from)} ถึง {thaiDay(student.to)}
                </div>
                <div className="display-6 fw-bold text-primary lh-1 mb-3">{pct(student.rate)}</div>
                <Table size="sm" className="mb-0" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  <tbody>
                    {[
                      ['วันที่มีข้อมูล', student.days],
                      ['ต้องเข้าละหมาด', student.expected],
                      ['เข้าละหมาด', student.attended],
                      ['ขาด', student.absent],
                      ['ได้รับยกเว้น / ป่วย', student.excused],
                      ['มาช้า', student.late],
                      ['ลืมบัตร', student.no_card],
                    ].map(([label, value]) => (
                      <tr key={label}>
                        <td className="text-secondary">{label}</td>
                        <td className="text-end fw-semibold">{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setStudent(null)}>ปิด</Button>
          </Modal.Footer>
        </Modal>

        {loading && (
          <div className="text-center text-secondary py-3">
            <Spinner size="sm" animation="border" className="me-2" />กำลังโหลด…
          </div>
        )}

        <Footer />
      </div>
    </>
  );
}
